import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Users } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import ErrorLogger from "@/utils/errorLogger";
import { CreateTeamForm } from "./CreateTeamForm";
import { TeamList } from "./TeamList";
import { ManageTeamDialog } from "./ManageTeamDialog";
import { EffectiveRole, normalizeRole } from "@/lib/team-roles";

interface Team {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
  owner_id?: string;
  memberCount?: number;
  myRole?: EffectiveRole;
}

export const TeamManagement = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, currentSession) => {
        const newAuthState = !!currentSession;
        setIsAuthenticated(newAuthState);
        if (newAuthState && !isAuthenticated) {
          setTimeout(() => fetchTeams(), 0);
        }
      }
    );

    const getInitialSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const hasSession = !!session;
      setIsAuthenticated(hasSession);
      if (hasSession) {
        fetchTeams();
      } else {
        setIsLoading(false);
      }
    };

    getInitialSession();
    return () => subscription.unsubscribe();
  }, []);

  const fetchTeams = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      setIsLoading(false);
      return;
    }
    const userId = session.user.id;

    setIsLoading(true);
    try {
      const { data: ownedTeams, error: ownedTeamsError } = await supabase
        .from("teams")
        .select("*")
        .eq("owner_id", userId);

      if (ownedTeamsError) throw new Error(ownedTeamsError.message);

      const { data: myMemberships, error: teamMembersError } = await supabase
        .from("team_members")
        .select("team_id, role")
        .eq("user_id", userId);

      if (teamMembersError) throw new Error(teamMembersError.message);

      let memberTeams: any[] = [];
      if (myMemberships && myMemberships.length > 0) {
        const teamIds = myMemberships.map((tm) => tm.team_id);
        const { data: memberTeamsData, error: memberTeamsError } = await supabase
          .from("teams")
          .select("*")
          .in("id", teamIds);

        if (memberTeamsError) {
          console.error("Error fetching member teams:", memberTeamsError);
        } else {
          memberTeams = memberTeamsData || [];
        }
      }

      let allTeams: Team[] = [...(ownedTeams || [])];
      memberTeams.forEach((team) => {
        if (!allTeams.some((t) => t.id === team.id)) allTeams.push(team);
      });

      if (allTeams.length === 0) {
        setTeams([]);
        setIsLoading(false);
        return;
      }

      const roleByTeam = new Map(
        (myMemberships || []).map((m) => [m.team_id, normalizeRole(m.role)])
      );

      // Member counts across the user's teams (roster is now readable to members)
      const teamIds = allTeams.map((t) => t.id);
      const { data: memberRows, error: countError } = await supabase
        .from("team_members")
        .select("team_id")
        .in("team_id", teamIds);

      const countMap: Record<string, number> = {};
      if (countError) {
        console.error("Error fetching team member counts:", countError);
      } else {
        memberRows?.forEach((member) => {
          countMap[member.team_id] = (countMap[member.team_id] || 0) + 1;
        });
      }

      setTeams(
        allTeams.map((team) => ({
          ...team,
          memberCount: countMap[team.id] || 0,
          myRole: (team.owner_id === userId ? "owner" : roleByTeam.get(team.id)) as EffectiveRole | undefined,
        }))
      );
    } catch (error: any) {
      console.error("Error in team fetching process:", error);
      ErrorLogger.logError(error, "Failed to load teams data");
      toast.error("Failed to load teams data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleManageTeam = (teamId: string) => {
    const team = teams.find((t) => t.id === teamId);
    if (team) {
      setSelectedTeam(team);
      setDialogOpen(true);
    }
  };

  return (
    <Card className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Team Management</h2>
          <p className="text-sm text-muted-foreground">
            Create teams and control what each member can do
          </p>
        </div>
        <Users className="h-6 w-6 text-muted-foreground" />
      </div>

      <div className="space-y-6">
        {isAuthenticated ? (
          <>
            <CreateTeamForm onTeamCreated={fetchTeams} />
            <TeamList teams={teams} isLoading={isLoading} onManageTeam={handleManageTeam} />
            <ManageTeamDialog
              team={selectedTeam}
              open={dialogOpen}
              onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) setSelectedTeam(null);
              }}
              onTeamUpdated={fetchTeams}
            />
          </>
        ) : (
          <p className="py-4 text-center text-muted-foreground">
            You must be logged in to manage teams
          </p>
        )}
      </div>
    </Card>
  );
};
